import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, type TransitionEvent } from "react";
import { createPortal } from "react-dom";
import { keepTaskNote, markTaskNoteDone, releaseTaskNote } from "./coffre";
import { Appear } from "./fade";
import { KanbanLink } from "./KanbanSync";
import { PropertyButton } from "./PropertyDesk";
import { watchKanbanLink } from "./kanbanLink";
import { FicheMark, warmFicheLogos } from "./FicheMark";
import { SpaceBoot } from "./SpaceBoot";
import { Modal } from "./Modal";
import { FichePagePicker } from "./FichePagePicker";
import { NotePad, type NotePadHandle } from "./NotePad";
import { ChoiceField, DateField, MultiChoiceField, TimeField } from "./pickers";
import { importanceColor, listProperties, taskBadges, type TaskProperty } from "./tasks";
import { saveSuivi } from "./suivi";
import type { CrmFiche } from "./types";
import {
  REMINDERS,
  blankWeekTask,
  createWeekTask,
  deleteWeekTask,
  isDraftWeekTask,
  loadWeekTasks,
  peekWeekTasks,
  orderRank,
  periodLabel,
  periodProgress,
  readPeriod,
  reminderLabel,
  reminderChannels,
  rollPendingTasks,
  reflectTaskDone,
  saveWeekTask,
  shiftTaskAgenda,
  layoutAfterMove,
  persistWeekMove,
  syncPeriodAgenda,
  syncPeriodTasks,
  occurrenceDates,
  taskClock,
  taskTimeLabel,
  taskDay,
  shareCaption,
  shareTone,
  shareIdsOf,
  isTaskOwner,
  taskShareMates,
  watchWeek,
  notifyWeekChange,
  isCrmAgendaMirror,
  kanbanBoardName,
  kanbanOriginProps,
  agendaMirrorAccent,
  taskMarquerAgenda,
  DAY_SPANS,
  daySpanOf,
  spanBounds,
  syncTaskAgendaLink,
  detachAgendaMirror,
  stripAgendaMirrorMeta,
  type PeriodRule,
  type DaySpan,
  type WeekTask,
} from "./week";
import { listAgendas, type AgendaChoice } from "./agenda";
import { OutsideShareField, outsideSent, outsideSet } from "./outside";
import { DayBlockButton } from "./DayBlock";
import { loadDayBlocks, type DayPart } from "./dayBlock";

export function WeekBoard({ fiches, onOpenFiche, focusId = "", onFocusDone }: { fiches: CrmFiche[]; onOpenFiche: (id: number) => void; focusId?: string; onFocusDone?: () => void }) {
  const [anchor, setAnchor] = useState(() => startOf(new Date()));
  const [tasks, setTasks] = useState<WeekTask[]>(() => peekWeekTasks() || []);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [catalog, setCatalog] = useState<TaskProperty[]>([]);
  const [open, setOpen] = useState<WeekTask | null>(null);
  const [over, setOver] = useState("");
  const [slot, setSlot] = useState<DropSlot | null>(null);
  const slotRef = useRef<DropSlot | null>(null);
  const [ghost, setGhost] = useState<DragGhost | null>(null);
  const ghostRef = useRef<DragGhost | null>(null);
  const [width, setWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const [motion, setMotion] = useState(false);
  const [jump, setJump] = useState<WeekJump | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [blocks, setBlocks] = useState<Record<string, DayPart[]>>({});
  const [blockLinked, setBlockLinked] = useState(true);
  const [blockReady, setBlockReady] = useState(true);
  const viewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusId || !ready) return;
    const task = tasks.find((row) => row.id === focusId);
    if (task) setOpen(task);
    onFocusDone?.();
  }, [focusId, ready, tasks, onFocusDone]);
  const lock = useRef(false);
  const pending = useRef(0);
  const carry = useRef(0);
  const unlockTimer = useRef(0);
  const painted = useRef(false);
  const boardGen = useRef(0);
  const blockGen = useRef(0);
  const fichesRef = useRef(fiches);
  fichesRef.current = fiches;

  const days = useMemo(() => Array.from({ length: LEAD + 13 }, (_, index) => addDays(anchor, index - LEAD)), [anchor]);
  const blockFrom = iso(days[0]);
  const blockTo = iso(days[days.length - 1]);
  const today = iso(new Date());
  const titleAnchor = jump?.to ?? anchor;
  const first = addDays(titleAnchor, 0);
  const last = addDays(titleAnchor, 3);
  const col = width > 0 ? (width - GAP * 5) / (4 + PEEK * 2) : 0;
  const step = col + GAP;
  const rest = (LEAD - 1) * step + col * (1 - PEEK);

  async function reload() {
    const mine = ++boardGen.current;
    const rows = await loadWeekTasks();
    if (mine !== boardGen.current) return;
    setTasks(rows);
  }

  async function refreshBoard() {
    if (refreshing) return;
    const mine = ++boardGen.current;
    setRefreshing(true);
    setError("");
    try {
      await rollPendingTasks().catch(() => 0);
      const stamp = ++blockGen.current;
      const [rows, props, held] = await Promise.all([loadWeekTasks(), listProperties(), loadDayBlocks(blockFrom, blockTo).catch(() => null)]);
      if (mine !== boardGen.current) return;
      setTasks(rows);
      setCatalog(props);
      if (held && stamp === blockGen.current) {
        setBlocks(held.days);
        setBlockLinked(held.linked);
        setBlockReady(held.ready);
      }
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Actualisation impossible.");
    } finally {
      setRefreshing(false);
    }
  }

  useLayoutEffect(() => {
    const node = viewRef.current;
    if (!node) return;
    const measure = () => {
      if (lock.current) return;
      setWidth(node.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ready]);

  useLayoutEffect(() => {
    if (!lock.current) setOffset(rest);
  }, [rest]);

  useEffect(() => {
    let alive = true;
    const refresh = (blank: boolean) => {
      const mine = ++boardGen.current;
      const hasRows = Boolean(peekWeekTasks()?.length || tasks.length);
      if (blank && !hasRows && !painted.current) setReady(false);
      void Promise.all([loadWeekTasks(), listProperties()])
        .then(async ([rows, props]) => {
          if (!alive || mine !== boardGen.current) return;
          setTasks(rows);
          setCatalog(props);
          setError("");
          setReady(true);
          if (!painted.current) {
            painted.current = true;
            void warmFicheLogos(fichesRef.current.map((fiche) => fiche.logo_path));
          }
          const moved = await rollPendingTasks().catch(() => 0);
          if (!alive || mine !== boardGen.current || moved < 1) return;
          const again = await loadWeekTasks();
          if (!alive || mine !== boardGen.current) return;
          setTasks(again);
        })
        .catch((reason: unknown) => {
          if (!alive || mine !== boardGen.current) return;
          if (blank) setError(reason instanceof Error ? reason.message : "Semaine indisponible.");
          setReady(true);
        });
    };
    refresh(true);
    const stop = watchKanbanLink(() => refresh(true));
    const stopWeek = watchWeek(() => refresh(false));
    return () => { alive = false; stop(); stopWeek(); };
  }, []);

  useEffect(() => {
    let alive = true;
    const stamp = ++blockGen.current;
    void loadDayBlocks(blockFrom, blockTo)
      .then((held) => {
        if (!alive || stamp !== blockGen.current) return;
        setBlocks(held.days);
        setBlockLinked(held.linked);
        setBlockReady(held.ready);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [blockFrom, blockTo]);

  function paintBlocks(day: string, parts: DayPart[], linked: boolean, ready: boolean) {
    blockGen.current += 1;
    setBlocks((current) => ({ ...current, [day]: parts }));
    setBlockLinked(linked);
    setBlockReady(ready);
  }

  async function change(task: WeekTask, patch: Partial<WeekTask> & { allDay?: number }) {
    setError("");
    try {
      await saveWeekTask(task, patch);
      const nextStatus = patch.status ?? task.status;
      if ((nextStatus === "done") !== (task.status === "done")) {
        const meta = patch.meta ?? task.meta;
        const fiche = fiches.find((row) => row.id === Number(meta.crmFicheId));
        if (fiche) {
          await reflectTaskDone(
            { ...task, title: patch.title ?? task.title, start: patch.start ?? task.start, meta },
            nextStatus === "done",
            fiche,
          );
        }
      }
      await reload();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
      throw reason;
    }
  }

  async function removeTask(task: WeekTask) {
    setError("");
    try {
      await deleteWeekTask(task);
      await reload();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible.");
    }
  }

  function grab(task: WeekTask, event: ReactPointerEvent<HTMLElement>) {
    if (event.button !== 0 || (event.target as HTMLElement).closest("[data-no-drag]")) return;
    const card = event.currentTarget;
    const startX = event.clientX;
    const startY = event.clientY;
    const width = card.offsetWidth;
    let moved = false;
    const blockSelect = (native: Event) => native.preventDefault();
    const move = (pointer: PointerEvent) => {
      if (!moved && Math.hypot(pointer.clientX - startX, pointer.clientY - startY) < 5) return;
      if (!moved) {
        moved = true;
        document.body.classList.add("crm-grabbing");
        window.getSelection()?.removeAllRanges();
      }
      pointer.preventDefault();
      const next = { id: task.id, title: task.title, x: pointer.clientX, y: pointer.clientY, width };
      ghostRef.current = next;
      setGhost(next);
      const raw = slotUnder(pointer.clientX, pointer.clientY, task.id);
      const nextSlot = raw ? clampSlot(task, raw, tasks) : null;
      slotRef.current = nextSlot;
      setSlot(nextSlot);
      setOver(nextSlot?.day || "");
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.removeEventListener("selectstart", blockSelect);
      document.body.classList.remove("crm-grabbing");
      window.getSelection()?.removeAllRanges();
      const chosen = slotRef.current;
      const id = ghostRef.current?.id || "";
      ghostRef.current = null;
      slotRef.current = null;
      setGhost(null);
      setSlot(null);
      setOver("");
      if (!moved || !id || !chosen) return;
      const stopClick = (native: Event) => {
        native.preventDefault();
        native.stopPropagation();
      };
      window.addEventListener("click", stopClick, true);
      window.setTimeout(() => window.removeEventListener("click", stopClick, true), 0);
      void place(id, chosen.day, chosen.index);
    };
    document.addEventListener("selectstart", blockSelect);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  async function place(id: string, day: string, index: number) {
    const next = layoutAfterMove(tasks, id, day, index);
    if (!next) return;
    boardGen.current += 1;
    setTasks(next);
    try {
      await persistWeekMove(tasks, next);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Déplacement impossible.");
      await reload().catch(() => undefined);
    }
  }

  function releaseSlide(delta: number) {
    if (unlockTimer.current) {
      window.clearTimeout(unlockTimer.current);
      unlockTimer.current = 0;
    }
    if (!pending.current) return;
    pending.current = 0;
    lock.current = false;
    setMotion(false);
    setAnchor((current) => addDays(current, delta));
    setOffset(rest);
    if (Math.abs(carry.current) >= WHEEL_NOTCH) {
      requestAnimationFrame(() => requestAnimationFrame(() => pumpWheel()));
    }
  }

  function jumpTo(to: Date) {
    if (lock.current) return;
    const delta = daySpan(anchor, to);
    if (delta === 0) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setAnchor(to);
      return;
    }
    lock.current = true;
    const next: WeekJump = { dir: delta > 0 ? 1 : -1, from: anchor, to, go: false };
    setJump(next);
    requestAnimationFrame(() => requestAnimationFrame(() => setJump({ ...next, go: true })));
  }

  function jumpSettled(event: TransitionEvent<HTMLDivElement>) {
    if (event.propertyName !== "transform" || event.target !== event.currentTarget || !jump?.go) return;
    setAnchor(jump.to);
    setJump(null);
    lock.current = false;
    setOffset(rest);
  }

  function slideBy(delta: number) {
    if (delta === 0 || lock.current || step === 0) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Hors du buffer du rail : même animation panneau que la vue mois (ex. Aujourd’hui).
    if (Math.abs(delta) > LEAD) {
      jumpTo(addDays(anchor, delta));
      return;
    }
    if (reduce) {
      setAnchor((current) => addDays(current, delta));
      return;
    }
    lock.current = true;
    pending.current = delta;
    setMotion(true);
    const next = rest + delta * step;
    if (unlockTimer.current) window.clearTimeout(unlockTimer.current);
    unlockTimer.current = window.setTimeout(() => releaseSlide(delta), 700);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setOffset(next));
    });
  }

  function pumpWheel() {
    if (lock.current || step === 0) return;
    const notches = Math.trunc(carry.current / WHEEL_NOTCH);
    if (!notches) return;
    const dir = Math.max(-4, Math.min(4, notches));
    carry.current -= dir * WHEEL_NOTCH;
    slideBy(dir);
  }

  function finish(event: TransitionEvent<HTMLDivElement>) {
    if (event.propertyName !== "transform" || event.target !== event.currentTarget || !pending.current) return;
    releaseSlide(pending.current);
  }

  useEffect(() => {
    const node = viewRef.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.shiftKey) return;
      event.preventDefault();
      carry.current += wheelPixels(event);
      pumpWheel();
    };
    node.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => node.removeEventListener("wheel", onWheel, true);
  }, [ready, step, rest]);

  const edgeVeil = Math.round(col * PEEK + 16);

  const shown = ready && col > 0;
  return (
    <section className="flex h-[calc(100dvh-5.5rem)] min-h-0 flex-col px-4 py-4">
      <div ref={viewRef} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {shown ? (
        <Appear className="flex min-h-0 flex-1 flex-col">
          <header className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
            <h2 className="crm-title min-w-0 text-lg font-semibold tracking-tight">{dayTitle(first)} – {dayTitle(last)}</h2>
            <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" onClick={() => jumpTo(startOf(new Date()))}>Aujourd’hui</button>
            <PropertyButton />
            <button
              type="button"
              className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 disabled:opacity-50"
              aria-label="Actualiser"
              title="Actualiser"
              disabled={refreshing}
              onClick={() => void refreshBoard()}
            >
              {refreshing ? <span className="crm-browse-loading-spin !h-3.5 !w-3.5" aria-hidden /> : <span className="material-icons-round text-[16px] leading-none">refresh</span>}
            </button>
            <KanbanLink />
          </header>
          {error ? <p className="mb-2 text-sm text-rose-700">{error}</p> : null}
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden" data-week-frame style={{ ["--week-veil" as string]: `${edgeVeil}px` }}>
            {jump ? (
              <div
                className="crm-week-track flex h-full w-[200%]"
                style={{ transform: `translate3d(${jump.dir === 1 ? (jump.go ? "-50%" : "0") : (jump.go ? "0" : "-50%")}, 0, 0)` }}
                onTransitionEnd={jumpSettled}
              >
                {(jump.dir === 1 ? [jump.from, jump.to] : [jump.to, jump.from]).map((date) => (
                  <div key={iso(date)} className="flex h-full w-1/2 shrink-0" style={{ gap: GAP }}>
                    {[-1, 0, 1, 2, 3, 4].map((index) => {
                      const day = addDays(date, index);
                      const key = iso(day);
                      const peek = index === -1 || index === 4;
                      const clip = peek ? col * PEEK : col;
                      return (
                        <div key={key} className="h-full min-h-0 shrink-0 overflow-hidden" style={{ width: clip }}>
                          <div className="h-full" style={{ width: col, transform: index === -1 ? `translateX(${-(col * (1 - PEEK))}px)` : undefined }}>
                            <DayColumn
                              day={day}
                              today={key === today}
                              hot={false}
                              rows={tasksOf(tasks, key)}
                              fiches={fiches}
                              catalog={catalog}
                              blocks={blocks[key] || []}
                              blockLinked={blockLinked}
                              blockReady={blockReady}
                              onBlocks={paintBlocks}
                              onCreate={(dayKey) => setOpen(blankWeekTask(dayKey))}
                              onOpen={setOpen}
                              onChange={change}
                              onRemove={removeTask}
                              onOpenFiche={onOpenFiche}
                              draggingId=""
                              insertAt={null}
                              onGrab={grab}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={motion ? "crm-week-track flex h-full" : "crm-week-track is-still flex h-full"}
                style={{ gap: GAP, transform: `translate3d(${-offset}px, 0, 0)`, width: days.length * col + (days.length - 1) * GAP }}
                onTransitionEnd={finish}
              >
                {days.map((day) => {
                  const key = iso(day);
                  return (
                    <div key={key} className="h-full min-h-0" style={{ width: col, flex: "0 0 auto" }}>
                      <DayColumn
                        day={day}
                        today={key === today}
                        hot={over === key}
                        rows={tasksOf(tasks, key)}
                        fiches={fiches}
                        catalog={catalog}
                        blocks={blocks[key] || []}
                        blockLinked={blockLinked}
                        blockReady={blockReady}
                        onBlocks={paintBlocks}
                        onCreate={(dayKey) => setOpen(blankWeekTask(dayKey))}
                        onOpen={setOpen}
                        onChange={change}
                        onRemove={removeTask}
                        onOpenFiche={onOpenFiche}
                        draggingId={ghost?.id || ""}
                        insertAt={over === key ? slot?.index ?? null : null}
                        onGrab={grab}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <div className="crm-week-fade is-left pointer-events-none absolute inset-y-0 left-0 z-[1]" style={{ width: edgeVeil }} />
            <div className="crm-week-fade pointer-events-none absolute inset-y-0 right-0 z-[1]" style={{ width: edgeVeil }} />
            <button type="button" className="crm-week-nav absolute left-1 top-1/2 z-20 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full" aria-label="Jour précédent" onClick={() => slideBy(-1)} disabled={Boolean(jump)}>
              <Chevron dir="left" />
            </button>
            <button type="button" className="crm-week-nav absolute right-1 top-1/2 z-20 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full" aria-label="Jour suivant" onClick={() => slideBy(1)} disabled={Boolean(jump)}>
              <Chevron dir="right" />
            </button>
            {ghost ? createPortal(
              <div
                className="crm-task-ghost pointer-events-none fixed z-[80] rounded-xl border border-slate-200 bg-white p-2 text-left text-sm font-normal shadow-[0_18px_40px_-18px_rgba(15,23,42,0.55)]"
                style={{ width: ghost.width, left: ghost.x + 12, top: ghost.y + 8, transform: "rotate(2deg)" }}
              >
                {ghost.title}
              </div>,
              document.body,
            ) : null}
          </div>
        </Appear>
      ) : (
        <SpaceBoot />
      )}
      </div>
      {open ? (
        <TaskSheet
          task={open}
          fiches={fiches}
          onClose={() => setOpen(null)}
          onOpenFiche={onOpenFiche}
          onSaved={async () => { setOpen(null); await reload(); }}
        />
      ) : null}
    </section>
  );
}

function DayColumn({
  day, today, hot, rows, fiches, catalog, blocks, blockLinked, blockReady, onBlocks, onCreate, onOpen, onChange, onRemove, onOpenFiche, draggingId, insertAt, onGrab,
}: {
  day: Date;
  today: boolean;
  hot: boolean;
  rows: WeekTask[];
  fiches: CrmFiche[];
  catalog: TaskProperty[];
  blocks: DayPart[];
  blockLinked: boolean;
  blockReady: boolean;
  onBlocks: (day: string, parts: DayPart[], linked: boolean, ready: boolean) => void;
  onCreate: (day: string) => void;
  onOpen: (task: WeekTask) => void;
  onChange: (task: WeekTask, patch: Partial<WeekTask> & { allDay?: number }) => Promise<void>;
  onRemove: (task: WeekTask) => Promise<void>;
  onOpenFiche: (id: number) => void;
  draggingId: string;
  insertAt: number | null;
  onGrab: (task: WeekTask, event: ReactPointerEvent<HTMLElement>) => void;
}) {
  const key = iso(day);
  const shown = rows.filter((task) => task.id !== draggingId);
  const listRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    const node = listRef.current;
    const body = bodyRef.current;
    if (!node || !body) return;
    const sync = () => setFade(node.scrollHeight - node.clientHeight - node.scrollTop > 12);
    sync();
    node.addEventListener("scroll", sync, { passive: true });
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    observer.observe(body);
    return () => {
      node.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [shown.length, insertAt]);

  return (
    <section
      data-day={key}
      className={["crm-card crm-day-col flex h-full min-h-0 flex-col rounded-2xl border p-2", today ? "is-today" : "", hot ? "is-hot border-sky-300" : "border-slate-200"].filter(Boolean).join(" ")}
    >
      <header className="border-b border-slate-200 px-1 pb-2">
        <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold capitalize">{day.toLocaleDateString("fr-FR", { weekday: "long" })}</p>
          <p className="text-xs text-slate-500">{day.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <span className="mr-2 text-xs font-semibold text-slate-400">{rows.length}</span>
          <DayBlockButton
            day={key}
            label={day.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            parts={blocks}
            linked={blockLinked}
            ready={blockReady}
            onChange={onBlocks}
          />
          <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-lg leading-none hover:bg-black/5" aria-label="Ajouter une tâche" onClick={() => onCreate(key)}>+</button>
        </div>
        </div>
      </header>
      <div className="relative mt-2 min-h-0 flex-1">
      <div ref={listRef} data-task-list className="crm-day-scroll h-full overflow-y-auto">
        <div ref={bodyRef} className="space-y-2">
        {shown.length === 0 && insertAt === null ? <p className="px-1 py-4 text-center text-xs text-slate-400">Aucune tâche</p> : null}
        {shown.map((task, index) => (
          <div key={task.id}>
            {insertAt === index ? <div className="crm-task-slot" /> : null}
            <Card task={task} fiches={fiches} catalog={catalog} onOpen={() => onOpen(task)} onChange={onChange} onRemove={onRemove} onOpenFiche={onOpenFiche} onGrab={onGrab} />
          </div>
        ))}
        {insertAt === shown.length ? <div className="crm-task-slot" /> : null}
        </div>
      </div>
      {fade ? <div className="crm-day-fade pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-16" /> : null}
      </div>
    </section>
  );
}

function cardSitsInVeil(node: HTMLElement, rect: DOMRect): boolean {
  const list = node.closest(".crm-day-scroll");
  if (list) {
    const view = list.getBoundingClientRect();
    if (rect.bottom > view.bottom - 52 && rect.top < view.bottom) return true;
  }
  const frame = node.closest("[data-week-frame]");
  if (frame instanceof HTMLElement) {
    const box = frame.getBoundingClientRect();
    const veil = Number.parseFloat(getComputedStyle(frame).getPropertyValue("--week-veil")) || 0;
    if (veil > 0 && (rect.left < box.left + veil || rect.right > box.right - veil)) return true;
  }
  return false;
}

function Card({ task, fiches, catalog, onOpen, onChange, onRemove, onOpenFiche, onGrab }: { task: WeekTask; fiches: CrmFiche[]; catalog: TaskProperty[]; onOpen: () => void; onChange: (task: WeekTask, patch: Partial<WeekTask> & { allDay?: number }) => Promise<void>; onRemove: (task: WeekTask) => Promise<void>; onOpenFiche: (id: number) => void; onGrab: (task: WeekTask, event: ReactPointerEvent<HTMLElement>) => void }) {
  const fiche = ficheOf(fiches, task);
  const reminder = reminderLabel(task);
  const channels = reminderChannels(task);
  const clock = taskTimeLabel(task);
  const period = readPeriod(task.meta);
  const done = task.status === "done";
  const paused = task.status === "pending" || task.meta.pending === true;
  const fromAgenda = isCrmAgendaMirror(task);
  const badges = taskBadges(task.meta, catalog).filter((badge) => {
    if (!paused) return true;
    const key = badge.key.toLowerCase();
    const label = badge.label.toLowerCase();
    if (key === "statut" || key === "pending" || key === "en_attente") return false;
    if (label.includes("attente") || label.includes("pause")) return false;
    return true;
  });
  const tint = importanceColor(task.meta, catalog);
  const progress = periodProgress(task.meta, task.status);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const accent = fromAgenda ? agendaMirrorAccent(task) : "";
  useEffect(() => {
    const node = cardRef.current;
    if (!node || done) return;
    let pop: HTMLElement | null = null;
    let watching = false;
    let leaving = false;
    let leaveTimer = 0;
    let leaveNode: HTMLElement | null = null;
    let leaveDone: ((event?: Event) => void) | null = null;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cancelLeave = () => {
      leaving = false;
      if (leaveTimer) window.clearTimeout(leaveTimer);
      leaveTimer = 0;
      if (leaveNode && leaveDone) leaveNode.removeEventListener("transitionend", leaveDone);
      leaveNode = null;
      leaveDone = null;
    };
    const finish = () => {
      cancelLeave();
      pop?.remove();
      pop = null;
      node.classList.remove("is-zoomed");
      if (!watching) return;
      window.removeEventListener("pointermove", track);
      watching = false;
    };
    const drop = (immediate = false) => {
      if (!pop) {
        finish();
        return;
      }
      if (immediate) {
        finish();
        return;
      }
      if (leaving) return;
      leaving = true;
      const flown = pop;
      const done = (event?: Event) => {
        if (event && event.target !== flown) return;
        if (event instanceof TransitionEvent && event.propertyName !== "transform") return;
        if (pop !== flown) return;
        finish();
      };
      leaveDone = done;
      leaveNode = flown;
      flown.classList.remove("is-on");
      flown.addEventListener("transitionend", done);
      leaveTimer = window.setTimeout(() => done(), 280);
    };
    const place = () => {
      const current = cardRef.current;
      if (!pop || !current) return;
      const rect = current.getBoundingClientRect();
      const view = current.closest(".crm-day-scroll")?.getBoundingClientRect();
      const outside = rect.width < 8 || rect.bottom < 0 || rect.top > window.innerHeight || (view ? rect.bottom < view.top + 4 || rect.top > view.bottom - 4 : false);
      if (outside) {
        drop();
        return;
      }
      pop.style.top = `${rect.top}px`;
      pop.style.left = `${rect.left}px`;
      pop.style.width = `${rect.width}px`;
      pop.style.height = `${rect.height}px`;
    };
    const track = (event: PointerEvent) => {
      const current = cardRef.current;
      if (!pop || !current || document.body.classList.contains("crm-grabbing")) {
        drop();
        return;
      }
      const rect = current.getBoundingClientRect();
      const pad = 8;
      const inside = event.clientX >= rect.left - pad && event.clientX <= rect.right + pad && event.clientY >= rect.top - pad && event.clientY <= rect.bottom + pad;
      if (!inside) {
        drop();
        return;
      }
      if (leaving && pop) {
        cancelLeave();
        pop.classList.add("is-on");
      }
      place();
    };
    const raise = () => {
      if (reduced || pop || document.body.classList.contains("crm-grabbing")) return;
      const rect = node.getBoundingClientRect();
      if (rect.width < 8 || rect.height < 8 || cardSitsInVeil(node, rect)) return;
      const clone = node.cloneNode(true) as HTMLElement;
      clone.classList.add("crm-task-pop");
      clone.setAttribute("aria-hidden", "true");
      clone.removeAttribute("data-task-id");
      clone.style.top = `${rect.top}px`;
      clone.style.left = `${rect.left}px`;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      document.body.appendChild(clone);
      node.classList.add("is-zoomed");
      pop = clone;
      window.addEventListener("pointermove", track);
      watching = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => pop?.classList.add("is-on"));
      });
    };
    const onScroll = () => place();
    const press = () => drop(true);
    node.addEventListener("pointerenter", raise);
    node.addEventListener("pointerdown", press);
    const scroller = node.closest(".crm-day-scroll");
    scroller?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      drop(true);
      node.removeEventListener("pointerenter", raise);
      node.removeEventListener("pointerdown", press);
      scroller?.removeEventListener("scroll", onScroll);
    };
  }, [done, task.id, task.title, task.status, paused]);
  return (
    <article
      ref={cardRef}
      data-task-id={task.id}
      onPointerDown={(event) => onGrab(task, event)}
      onDragStart={(event) => event.preventDefault()}
      className={
        done
          ? "crm-task-card is-done cursor-grab rounded-lg border border-slate-200/80 bg-white/60 px-2 py-1 text-left"
          : fromAgenda
            ? "crm-task-card is-lift is-agenda cursor-grab rounded-xl border border-slate-200 bg-white/80 p-2 text-left shadow-sm"
            : paused
              ? "crm-task-card is-lift is-paused cursor-grab rounded-xl p-2 text-left"
              : "crm-task-card is-lift cursor-grab rounded-xl border border-slate-200 bg-white/80 p-2 text-left shadow-sm"
      }
      style={
        done
          ? undefined
          : fromAgenda
            ? ({ ["--agenda-mirror-accent"]: accent } as CSSProperties)
            : !paused && tint
              ? { borderLeftWidth: 3, borderLeftColor: tint }
              : undefined
      }
    >
      {done ? (
        <div className="flex items-center gap-1.5">
          <button type="button" className="crm-task-body min-w-0 flex-1 text-left" onClick={onOpen}>
            <p className="crm-task-title truncate text-sm font-normal line-through opacity-70">{task.title}</p>
            {shareCaption(task) || kanbanBoardName(task.meta) ? <p className={["truncate text-[10px] font-medium", shareTone(task) === "in" ? "text-violet-700" : shareTone(task) === "out" ? "text-sky-700" : "text-slate-500"].join(" ")}>{[kanbanBoardName(task.meta) ? `Kanban · ${kanbanBoardName(task.meta)}` : "", shareCaption(task)].filter(Boolean).join(" · ")}</p> : null}
          </button>
          <IconButton label="Rouvrir" className="bg-amber-500 text-white" onClick={() => onChange(task, { status: "todo", meta: { ...task.meta, statut: "todo", pending: false } }).catch(() => undefined)}>
            replay
          </IconButton>
        </div>
      ) : (
        <>
          <button type="button" className="crm-task-body w-full text-left" onClick={onOpen}>
            <p className="crm-task-title flex items-start gap-1.5 text-sm font-normal">
              {fromAgenda ? <span className="material-icons-round mt-0.5 shrink-0 text-[16px] leading-none" style={{ fontSize: 16, width: 16, height: 16, color: accent }} title="Événement synchronisé depuis l’agenda">event</span> : null}
              <span className="min-w-0 flex-1">{task.title}</span>
            </p>
            {badges.length > 0 ? (
              <p className="crm-task-badges mt-1 flex flex-wrap gap-1">
                {badges.map((badge) => (
                  <span key={badge.key} className="rounded-md px-1.5 py-0.5 text-[10px] font-medium" style={{ background: badge.bg, color: badge.text }}>{badge.label}</span>
                ))}
              </p>
            ) : null}
            <p className="mt-1 flex flex-wrap gap-1">
              {kanbanBoardName(task.meta) ? <span className="crm-task-from-kanban rounded-full px-1.5 py-0.5 text-[10px] font-medium" title={`Envoyée depuis le kanban ${kanbanBoardName(task.meta)}`}>Kanban · {kanbanBoardName(task.meta)}</span> : null}
              {kanbanOriginProps(task.meta).filter((row) => !badges.some((badge) => badge.label === row.value)).map((row) => (
                <span key={`${row.label}:${row.value}`} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700" title={row.label}>
                  <span className="h-2 w-2 rounded-full" style={{ background: row.color }} />
                  {row.value}
                </span>
              ))}
              {shareCaption(task) ? <span className={shareTone(task) === "in" ? "rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700" : "rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"}>{shareCaption(task)}</span> : null}
              {paused ? <span className="crm-task-paused-tag rounded-full px-1.5 py-0.5 text-[10px] font-medium">En attente</span> : null}
              {clock ? <span className={fromAgenda ? "crm-task-agenda-time rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" : "rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"}>{clock}</span> : null}
              {reminder ? (
                <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" title={[reminder, channels.mail ? "E-mail" : "", channels.sms ? "SMS" : ""].filter(Boolean).join(" · ")}>
                  {reminder}
                  {channels.mail || channels.sms ? (
                    <span className="ml-1 opacity-80">
                      {[channels.mail ? "mail" : "", channels.sms ? "SMS" : ""].filter(Boolean).join("+")}
                    </span>
                  ) : null}
                </span>
              ) : null}
              {!fromAgenda && period ? <span className="crm-soft rounded-full border px-1.5 py-0.5 text-[10px] font-medium">{periodLabel(period)}</span> : null}
              {!fromAgenda && progress ? <span className="crm-soft rounded-full border px-1.5 py-0.5 text-[10px] font-medium">{Number(task.meta.occurrenceIndex || 0) + 1}/{Number(task.meta.occurrenceTotal || 0)}</span> : null}
            </p>
          </button>
          {fiche ? (
            <button type="button" data-no-drag className="mt-1.5 flex max-w-full items-center gap-1.5" title={fiche.name} onClick={() => onOpenFiche(fiche.id)}>
              <FicheMark path={fiche.logo_path} name={fiche.name} bare={fiche.scope === "interne"} className="h-5 w-5 text-[10px]" />
              <span className="truncate text-xs font-normal crm-ink">{fiche.name}</span>
            </button>
          ) : task.client_name ? <p className="mt-1 truncate text-xs text-slate-500">{task.client_name}</p> : null}
          <div className="mt-1.5 flex items-center justify-end gap-1.5">
            <IconButton label="Clore la tâche" className="bg-emerald-500 text-white" onClick={() => onChange(task, { status: "done", meta: { ...task.meta, statut: "done", pending: false } }).catch(() => undefined)}>
              check
            </IconButton>
            <IconButton label={paused ? "Reprendre la tâche" : "Mettre en attente"} className={paused ? "bg-amber-500 text-white" : "bg-slate-500 text-white"} onClick={() => onChange(task, { status: paused ? "todo" : "pending", meta: { ...task.meta, statut: paused ? "todo" : "pending", pending: !paused } }).catch(() => undefined)}>
              {paused ? "play_arrow" : "pause"}
            </IconButton>
            {!fromAgenda ? (
              <IconButton label="Périodicité" className="crm-fill text-white" onClick={() => setPeriodOpen(true)}>
                event_repeat
              </IconButton>
            ) : null}
            {isTaskOwner(task) ? (
            <IconButton label={confirmDrop ? "Confirmer la suppression" : "Supprimer"} className={confirmDrop ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600"} onClick={() => { if (!confirmDrop) { setConfirmDrop(true); return; } return onRemove(task).catch(() => undefined); }}>
              delete
            </IconButton>
            ) : null}
          </div>
          {periodOpen && !fromAgenda ? <PeriodSheet task={task} onClose={() => setPeriodOpen(false)} onChange={onChange} /> : null}
        </>
      )}
    </article>
  );
}

function IconButton({ label, className, onClick, children }: { label: string; className: string; onClick: () => void | Promise<void>; children: string }) {
  const [busy, setBusy] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      aria-busy={busy}
      data-no-drag
      className={`crm-task-icon grid h-5 w-5 place-items-center rounded ${pressed ? "is-pressed" : ""} ${className}`}
      aria-label={label}
      title={label}
      onPointerDown={(event) => {
        event.stopPropagation();
        setPressed(true);
        window.setTimeout(() => setPressed(false), 160);
      }}
      onClick={(event) => {
        event.stopPropagation();
        const result = onClick();
        if (result && typeof result.then === "function") {
          setBusy(true);
          void result.finally(() => setBusy(false));
        }
      }}
    >
      {busy ? <span className="crm-task-spin" /> : <span className="material-icons-round text-[12px] leading-none" style={{ fontSize: 12, width: 12, height: 12 }}>{children}</span>}
    </button>
  );
}

function PeriodSheet({ task, onClose, onChange }: { task: WeekTask; onClose: () => void; onChange: (task: WeekTask, patch: Partial<WeekTask>) => Promise<void> }) {
  const current = readPeriod(task.meta);
  const [freq, setFreq] = useState<PeriodRule["freq"]>(current?.freq || "weekly");
  const [interval, setInterval] = useState(current?.interval || 1);
  const [endType, setEndType] = useState<PeriodRule["endType"]>(current?.endType || "never");
  const [until, setUntil] = useState(current?.until || defaultUntil(taskDay(task)));
  const [count, setCount] = useState(current?.count || 10);
  const [place, setPlace] = useState(current?.placeAgenda || false);
  const [calendarId, setCalendarId] = useState(String(task.meta.agendaCalendarId || task.meta.agenda_calendar_id || ""));
  const [agendas, setAgendas] = useState<AgendaChoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    void listAgendas().then((rows) => {
      if (!alive) return;
      setAgendas(rows);
      setCalendarId((currentId) => currentId || rows[0]?.id || "");
    }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  async function save(close: () => void, off = false) {
    setBusy(true);
    setError("");
    setBusyLabel(off ? "Désactivation de la récurrence…" : "Préparation des occurrences…");
    const seriesId = String(task.meta.seriesId || task.id);
    const startDate = current?.startDate || taskDay(task);
    const rule: PeriodRule | null = off ? null : {
      freq,
      interval: Math.max(1, Math.min(99, interval)),
      endType,
      until: endType === "until" ? until : undefined,
      count: endType === "count" ? Math.max(1, count) : undefined,
      startDate,
      occurrenceIndex: current?.occurrenceIndex || 0,
      placeAgenda: place,
    };
    const dates = rule ? occurrenceDates(rule, startDate) : [];
    const day = taskDay(task);
    const index = dates.indexOf(day);
    const meta: Record<string, unknown> = {
      ...task.meta,
      seriesId,
      repeat: rule,
      repeatStartDate: rule?.startDate || null,
      marquer_agenda: Boolean(rule?.placeAgenda),
      agendaCalendarId: calendarId,
      agenda_calendar_id: calendarId,
      occurrenceIndex: index >= 0 ? index : 0,
      occurrenceTotal: dates.length,
      seriesUntil: dates.length ? dates[dates.length - 1] : null,
    };
    if (!rule) {
      delete meta.repeat;
      delete meta.seriesId;
      delete meta.occurrenceIndex;
      delete meta.occurrenceTotal;
      delete meta.seriesUntil;
      delete meta.repeatStartDate;
      delete meta.marquer_agenda;
    }
    try {
      const next = { ...task, meta };
      await onChange(task, { meta });
      if (rule?.placeAgenda) {
        setBusyLabel(dates.length > 1 ? `Création de ${dates.length} occurrences dans le CRM…` : "Mise à jour de la tâche…");
        await syncPeriodTasks(next, rule);
        setBusyLabel("Placement des occurrences dans l’agenda…");
        await syncPeriodAgenda(next, rule, calendarId, seriesId);
      } else {
        setBusyLabel(off || current?.placeAgenda ? "Mise à jour de l’agenda…" : dates.length > 1 ? `Création de ${dates.length} occurrences dans le CRM…` : "Mise à jour de la tâche…");
        await syncPeriodAgenda(next, rule, calendarId, seriesId);
        setBusyLabel(off ? "Nettoyage des prochaines tâches…" : dates.length > 1 ? `Création de ${dates.length} occurrences dans le CRM…` : "Mise à jour de la tâche…");
        await syncPeriodTasks(next, rule);
      }
      setBusyLabel("Presque terminé…");
      close();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
      setBusy(false);
      setBusyLabel("");
    }
  }

  const unit = freq === "monthly" ? (interval > 1 ? "mois" : "mois") : (interval > 1 ? "semaines" : "semaine");

  return (
    <Modal onClose={() => { if (!busy) onClose(); }} closeDisabled={busy} sheetClass="crm-pop crm-task-sheet relative w-full max-w-lg rounded-2xl border border-slate-200 px-5 pt-5 pb-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          {busy ? (
            <div className="crm-browse-loading" role="status" aria-live="polite">
              <span className="crm-browse-loading-spin" aria-hidden />
              <span>{busyLabel || "Préparation…"}</span>
              <span className="max-w-[16rem] text-center text-xs font-normal opacity-70">Merci de patienter, les prochaines échéances sont en cours de création.</span>
            </div>
          ) : null}
          <p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Périodicité</p>
          <h2 className="mt-1 shrink-0 text-lg font-semibold">{task.title}</h2>
          <div className="crm-task-sheet-body mt-4 space-y-3 pr-1">
            <section className="rounded-2xl border border-slate-200 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Rythme</p>
              <div className="mt-2 flex gap-2">
                <button type="button" className={freq === "weekly" ? "rounded-full crm-fill px-3 py-1.5 text-sm font-semibold text-white" : "crm-pill rounded-full px-3 py-1.5 text-sm font-semibold"} onClick={() => setFreq("weekly")} disabled={busy}>Semaine</button>
                <button type="button" className={freq === "monthly" ? "rounded-full crm-fill px-3 py-1.5 text-sm font-semibold text-white" : "crm-pill rounded-full px-3 py-1.5 text-sm font-semibold"} onClick={() => setFreq("monthly")} disabled={busy}>Mois</button>
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                Toutes les
                <input type="number" min={1} max={99} className="crm-field w-16 rounded-xl border px-2 py-1.5 text-center" value={interval} disabled={busy} onChange={(event) => setInterval(Number(event.target.value) || 1)} />
                {unit}
              </label>
            </section>
            <section className="rounded-2xl border border-slate-200 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Fin</p>
              <div className="mt-2 space-y-2">
                <Switch on={endType === "never"} label="Sans date de fin" onClick={() => { if (!busy) setEndType("never"); }} />
                <div className="rounded-xl border border-slate-200 px-3 py-2">
                  <Switch on={endType === "until"} label="Jusqu’à une date" onClick={() => { if (!busy) setEndType("until"); }} />
                  {endType === "until" ? (
                    <div className="mt-2"><DateField value={until} onChange={(value) => { setUntil(value); setEndType("until"); }} /></div>
                  ) : null}
                </div>
                <div className="rounded-xl border border-slate-200 px-3 py-2">
                  <Switch on={endType === "count"} label="Après un nombre de fois" onClick={() => { if (!busy) setEndType("count"); }} />
                  {endType === "count" ? (
                    <label className="mt-2 flex items-center gap-2 text-sm">
                      <input type="number" min={1} max={999} className="crm-field w-16 rounded-xl border px-2 py-1 text-center" value={count} disabled={busy} onChange={(event) => { setCount(Number(event.target.value) || 1); setEndType("count"); }} />
                      fois
                    </label>
                  ) : null}
                </div>
              </div>
            </section>
            <section className="rounded-2xl border border-slate-200 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Occurrences CRM</p>
              <p className="mt-2 text-xs text-slate-500">
                {freq === "weekly"
                  ? `Le CRM pose une tâche distincte à chaque échéance${interval > 1 ? ` (toutes les ${interval} semaines)` : ""} : jusqu’à ${endType === "count" ? `${count} fois` : endType === "until" ? "la date de fin" : "52 occurrences (~1 an)"}. Clore une tâche ne touche pas les suivantes.`
                  : `Le CRM pose une tâche distincte à chaque échéance${interval > 1 ? ` (tous les ${interval} mois)` : ""} : jusqu’à ${endType === "count" ? `${count} fois` : endType === "until" ? "la date de fin" : "52 occurrences (~1 an)"}. Clore une tâche ne touche pas les suivantes.`}
              </p>
              <div className="mt-3">
                <Switch on={place} label="Placer aussi les occurrences dans l’agenda" onClick={() => { if (!busy) setPlace((value) => !value); }} />
              </div>
              {place ? (
                <div className="mt-2">
                  <ChoiceField value={calendarId} placeholder={agendas.length === 0 ? "Aucun agenda" : "Choisir"} options={agendas.map((item) => ({ value: item.id, label: item.name }))} onChange={setCalendarId} />
                </div>
              ) : null}
            </section>
            {error ? <p className="text-sm text-rose-700">{error}</p> : null}
          </div>
          <div className="crm-task-sheet-foot flex flex-wrap items-center gap-2">
            {current ? <button type="button" className="rounded-full border border-rose-200 px-3 py-1.5 text-sm font-semibold text-rose-700" disabled={busy} onClick={() => void save(close, true)}>{busy && busyLabel.includes("Désactivation") ? "Désactivation…" : "Désactiver la récurrence"}</button> : null}
            <button type="button" className="ml-auto inline-flex items-center gap-1.5 rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void save(close)}>
              <span className="material-icons-round text-[16px] leading-none" aria-hidden>save</span>
              {busy ? "En cours…" : "Enregistrer"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function TaskSheet({ task, fiches, onClose, onOpenFiche, onSaved, phone = false }: { task: WeekTask; fiches: CrmFiche[]; onClose: () => void; onOpenFiche: (id: number) => void; onSaved: () => Promise<void>; phone?: boolean }) {
  const draft = isDraftWeekTask(task);
  const fiche = ficheOf(fiches, task);
  const reminded = reminderValue(task) !== "";
  const mailOn = flag(task.meta.notify_mail);
  const smsOn = flag(task.meta.notify_sms);
  const [title, setTitle] = useState(task.title);
  const [shareIds, setShareIds] = useState<number[]>(() => shareIdsOf(task));
  const [outsideIds, setOutsideIds] = useState<number[]>([]);
  const owner = isTaskOwner(task);
  const mates = taskShareMates();
  const [status, setStatus] = useState(task.status);
  const [note, setNote] = useState(task.description);
  const [padSeed, setPadSeed] = useState(task.description);
  const [padTick, setPadTick] = useState(0);
  const [client, setClient] = useState(fiche?.id ? String(fiche.id) : "");
  const [day, setDay] = useState(taskDay(task));
  const [span, setSpan] = useState<DaySpan>(daySpanOf(task));
  const [time, setTime] = useState(taskClock(task) || "09:00");
  const [reminder, setReminder] = useState(reminderValue(task));
  const [mail, setMail] = useState(mailOn || (reminded && !smsOn));
  const [sms, setSms] = useState(smsOn);
  const [catalog, setCatalog] = useState<TaskProperty[]>([]);
  const [props, setProps] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  const [error, setError] = useState("");
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [meta, setMeta] = useState(task.meta);
  const [detailsOpen, setDetailsOpen] = useState(!draft);
  const pad = useRef<NotePadHandle>(null);
  useEffect(() => {
    if (!phone) return;
    const root = document.documentElement;
    const sync = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;
      root.style.setProperty("--crm-vv-top", `${viewport.offsetTop}px`);
      root.style.setProperty("--crm-vv-height", `${viewport.height}px`);
    };
    sync();
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
      window.removeEventListener("orientationchange", sync);
      root.style.removeProperty("--crm-vv-top");
      root.style.removeProperty("--crm-vv-height");
    };
  }, [phone]);
  const saving = useRef(false);
  const outsideTouched = useRef(false);
  const outsideReady = useRef(draft);
  const wasMirror = isCrmAgendaMirror(task);
  const [followAgenda, setFollowAgenda] = useState(wasMirror);
  const [placeAgenda, setPlaceAgenda] = useState(() => taskMarquerAgenda(task.meta) || wasMirror);
  const [agendaCal, setAgendaCal] = useState(String(task.meta.agendaCalendarId || task.meta.agenda_calendar_id || ""));
  const [agendas, setAgendas] = useState<AgendaChoice[]>([]);

  useEffect(() => {
    setNote(task.description);
    setPadSeed(task.description);
    setPadTick(0);
  }, [task.id, task.description]);

  useEffect(() => {
    outsideTouched.current = false;
    if (draft || !owner) {
      outsideReady.current = true;
      return;
    }
    outsideReady.current = false;
    let alive = true;
    void outsideSent("week", task.id).then((ids) => {
      if (!alive) return;
      if (!outsideTouched.current) setOutsideIds(ids);
      outsideReady.current = true;
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [draft, owner, task.id]);

  useEffect(() => {
    setMeta(task.meta);
    setShareIds(shareIdsOf(task));
    setStatus(task.status);
    setDetailsOpen(!isDraftWeekTask(task));
    const mirror = isCrmAgendaMirror(task);
    setFollowAgenda(mirror);
    setPlaceAgenda(taskMarquerAgenda(task.meta) || mirror);
    setAgendaCal(String(task.meta.agendaCalendarId || task.meta.agenda_calendar_id || ""));
  }, [task]);

  useEffect(() => {
    let alive = true;
    void listAgendas().then((rows) => {
      if (!alive) return;
      setAgendas(rows);
      setAgendaCal((current) => current || rows[0]?.id || "");
    }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    void listProperties().then((rows) => {
      if (!alive) return;
      const usable = rows.filter((prop) => !RESERVED.has(prop.key));
      setCatalog(usable);
      const next = Object.fromEntries(usable.map((prop) => [prop.key, metaText(task.meta[prop.key])]));
      setProps(next);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [task]);

  function setProp(key: string, value: string) {
    setProps((current) => ({ ...current, [key]: value }));
    if (key === "statut") setStatus(taskStatusFromProp(value));
  }

  const period = readPeriod(meta);
  const showPlaceAgenda = !wasMirror || !followAgenda;

  async function save(close: () => void, as?: "done" | "todo") {
    const name = title.trim();
    if (!name) { setError("Un titre est nécessaire."); return; }
    if (showPlaceAgenda && placeAgenda && !agendaCal.trim()) {
      setError("Choisis un agenda pour y placer la tâche.");
      return;
    }
    if (saving.current) return;
    saving.current = true;
    let description = note;
    if (!(draft && !detailsOpen)) {
      try {
        const flushed = await pad.current?.flush();
        if (typeof flushed === "string") description = flushed;
      } catch {
        description = note;
      }
    }
    setBusy(true);
    setBusyLabel(as === "done" ? "Clôture…" : as === "todo" ? "Réouverture…" : draft ? "Création de la tâche…" : "Enregistrement…");
    setError("");
    const minutes = reminder === "" ? null : Number(reminder);
    const picked = fiches.find((row) => String(row.id) === client);
    let metaNext: Record<string, unknown> = {
      ...meta,
      reminder: minutes,
      notify_mail: minutes !== null && mail,
      notify_sms: minutes !== null && sms,
    };
    catalog.forEach((prop) => { metaNext[prop.key] = props[prop.key] || null; });
    metaNext.crmFicheId = picked ? picked.id : null;
    if (owner) metaNext.sharedWith = shareIds;
    const statutProp = String(props.statut || "");
    let nextStatus = statutProp ? taskStatusFromProp(statutProp) : (status === "done" || status === "pending" || status === "inprogress" ? status : "todo");
    metaNext.pending = nextStatus === "pending";
    if (!statutProp) metaNext.statut = null;
    if (as === "done" || as === "todo") {
      nextStatus = as;
      metaNext.statut = as;
      metaNext.pending = false;
    }

    const detach = wasMirror && !followAgenda;
    if (detach) {
      metaNext = stripAgendaMirrorMeta(metaNext);
      metaNext.marquer_agenda = placeAgenda ? true : null;
      if (placeAgenda && agendaCal) {
        metaNext.agendaCalendarId = agendaCal;
        metaNext.agenda_calendar_id = agendaCal;
      }
    } else if (wasMirror && followAgenda) {
      metaNext.agendaMirror = true;
      metaNext.marquer_agenda = null;
    } else if (placeAgenda) {
      metaNext.marquer_agenda = true;
      metaNext.agendaCalendarId = agendaCal;
      metaNext.agenda_calendar_id = agendaCal;
    } else {
      metaNext.marquer_agenda = null;
    }

    const previous = fiches.find((row) => row.id === Number(meta.crmFicheId));
    const due = day || taskDay(task);
    const bounds = spanBounds(span, time || "09:00");
    const start = `${due} ${bounds.start}:00`;
    metaNext.allDay = 0;
    metaNext.daySpan = span;
    metaNext.agendaEnd = `${bounds.end}:00`;
    try {
      let taskId = task.id;
      if (draft) {
        taskId = await createWeekTask({
          title: name.slice(0, 180),
          day: due,
          description,
          status: nextStatus,
          start,
          allDay: 0,
          client_name: picked?.name || "",
          meta: metaNext,
        });
      } else {
        await saveWeekTask(task, {
          title: name.slice(0, 180),
          description,
          status: nextStatus,
          start,
          allDay: 0,
          client_name: picked?.name || "",
          meta: metaNext,
        });
      }
      if (owner && (outsideTouched.current || outsideReady.current)) await outsideSet("week", taskId, outsideIds);
      const saved: WeekTask = {
        ...task,
        id: taskId,
        title: name.slice(0, 180),
        description,
        status: nextStatus,
        start,
        client_name: picked?.name || "",
        meta: metaNext,
      };
      if (detach) {
        setBusyLabel("Détachement de l’agenda…");
        await detachAgendaMirror(task);
      }
      const wantPlace = (!wasMirror || detach) && placeAgenda;
      const wantClear = (!wasMirror || detach) && !placeAgenda && taskMarquerAgenda(meta);
      const alreadyLinked = (wasMirror && followAgenda) || (!wantClear && (taskMarquerAgenda(task.meta) || Boolean(readPeriod(task.meta)?.placeAgenda)));
      if (alreadyLinked && task.start !== start) {
        setBusyLabel("Mise à jour de l’agenda…");
        const shifted = await shiftTaskAgenda(task, start);
        if (!shifted) throw new Error("L’agenda n’a pas suivi le déplacement.");
      }
      if (wantPlace || wantClear) {
        setBusyLabel(wantPlace ? "Publication dans l’agenda…" : "Retrait de l’agenda…");
        await syncTaskAgendaLink(saved, {
          place: wantPlace,
          calendarId: agendaCal,
          previousDay: taskDay(task),
          seriesId: String(task.meta.seriesId || ""),
        });
      }
      if (!draft && previous && previous.id !== picked?.id) {
        setBusyLabel("Mise à jour de la fiche…");
        await releaseTaskNote(previous.coffre_path, previous.coffre_owner_id || 0, taskId);
      }
      if (picked) {
        setBusyLabel("Liaison à la fiche…");
        const dueDay = day || taskDay(task);
        const when = formatNoteWhen(dueDay, span, bounds);
        const detail = plainNote(description);
        const noteBody = detail && detail !== name ? `${when}\n\n${detail}` : when;
        await keepTaskNote(picked.coffre_path, picked.coffre_owner_id || 0, taskId, name, noteBody, !draft && previous?.id === picked.id);
        const crossed = (nextStatus === "done") !== (task.status === "done");
        if (crossed) {
          await markTaskNoteDone(picked.coffre_path, picked.coffre_owner_id || 0, taskId, nextStatus === "done").catch(() => undefined);
        }
        await saveSuivi(picked.id, {
          action: "sync",
          note_key: name.slice(0, 180),
          title: name.slice(0, 180),
          kind: "meeting",
          ...(crossed ? { done: nextStatus === "done" } : {}),
          due_on: dueDay,
        }).catch(() => undefined);
      }
      setBusyLabel("Presque terminé…");
      await onSaved();
      close();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
      saving.current = false;
      setBusy(false);
      setBusyLabel("");
    }
  }

  async function openPeriod() {
    const flushed = await pad.current?.flush();
    if (typeof flushed === "string") setNote(flushed);
    setPeriodOpen(true);
  }

  async function remove(close: () => void) {
    if (draft) {
      close();
      return;
    }
    setBusy(true);
    setBusyLabel("Suppression…");
    try {
      await deleteWeekTask(task);
      await onSaved();
      close();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible.");
      setBusy(false);
      setBusyLabel("");
    }
  }

  async function clearPeriod() {
    setBusy(true);
    setBusyLabel("Désactivation de la récurrence…");
    setError("");
    const seriesId = String(meta.seriesId || task.id);
    const agendaCal = String(meta.agendaCalendarId || meta.agenda_calendar_id || "");
    const nextMeta: Record<string, unknown> = { ...meta };
    delete nextMeta.repeat;
    delete nextMeta.seriesId;
    delete nextMeta.occurrenceIndex;
    delete nextMeta.occurrenceTotal;
    delete nextMeta.seriesUntil;
    delete nextMeta.repeatStartDate;
    delete nextMeta.marquer_agenda;
    try {
      const next = { ...task, meta: nextMeta };
      await saveWeekTask(task, { meta: nextMeta });
      setBusyLabel("Mise à jour de l’agenda…");
      await syncPeriodAgenda(next, null, agendaCal, seriesId);
      setBusyLabel("Nettoyage des prochaines tâches…");
      await syncPeriodTasks(next, null);
      setMeta(nextMeta);
      setConfirmOff(false);
      setBusy(false);
      setBusyLabel("");
      notifyWeekChange();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Désactivation impossible.");
      setBusy(false);
      setBusyLabel("");
    }
  }

  const linked = fiches.find((row) => String(row.id) === client);
  const compactCreate = draft && !detailsOpen;

  return (
    <Modal
      onClose={() => { if (!busy) onClose(); }}
      closeDisabled={busy}
      closeGutter={!phone}
      sheetClass={`crm-pop crm-task-sheet relative flex w-full flex-col rounded-2xl border border-slate-200 px-5 pt-5 pb-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)] ${phone ? "is-phone" : ""} ${!phone && (detailsOpen || !draft) ? "is-wide" : ""} ${compactCreate ? "is-compact" : ""}`}
    >
      {(close) => (
        <>
          {busy ? (
            <div className="crm-browse-loading" role="status" aria-live="polite">
              <span className="crm-browse-loading-spin" aria-hidden />
              <span>{busyLabel || "En cours…"}</span>
            </div>
          ) : null}
          <p className={["shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em]", !draft && shareTone(task) === "in" ? "text-violet-700" : !draft && shareTone(task) === "out" ? "text-sky-700" : "text-slate-500"].join(" ")}>{draft ? "Nouvelle tâche" : shareCaption(task) || "Tâche"}</p>
          {!draft && kanbanBoardName(task.meta) ? <p className="crm-task-from-kanban mt-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium">Depuis le kanban {kanbanBoardName(task.meta)}</p> : null}
          <input
            className="crm-field crm-task-title-input mt-2 w-full shrink-0 rounded-xl px-3 py-2 text-lg font-normal"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoFocus={draft}
            placeholder="Titre de la tâche"
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.shiftKey) return;
              event.preventDefault();
              if (compactCreate) void save(close);
            }}
          />
          {compactCreate && owner && mates.length > 0 ? (
            <div className="mt-3">
              <ShareLine plain mates={mates} shareIds={shareIds} setShareIds={setShareIds} />
            </div>
          ) : null}
          <div className={`crm-task-create-step ${compactCreate ? "is-visible" : "is-hidden"}`}>
            <p className="mt-2 text-xs text-slate-500">Enregistrer pose une tâche simple. Poursuivre ouvre les détails.</p>
            {error && compactCreate ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
            <div className="crm-task-sheet-foot mt-4 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-full border border-slate-200 px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
                disabled={busy}
                onClick={() => {
                  if (!title.trim()) { setError("Un titre est nécessaire."); return; }
                  setError("");
                  setDetailsOpen(true);
                }}
              >
                Poursuivre
              </button>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void save(close)}>
                <span className="material-icons-round text-[16px] leading-none" aria-hidden>save</span>
                {busy ? "En cours…" : "Enregistrer"}
              </button>
            </div>
          </div>

          <div className={`crm-task-create-details ${detailsOpen || !draft ? "is-open" : ""}`} aria-hidden={compactCreate}>
            <div className="crm-task-create-details-inner">
              {phone ? (
                <>
                  <div className="crm-task-sheet-body mt-3 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Jour"><DateField value={day} onChange={setDay} /></Field>
                      <Field label="Heure">
                        <div className={span === "time" ? "" : "pointer-events-none opacity-40"}>
                          <TimeField value={time} onChange={setTime} />
                        </div>
                      </Field>
                    </div>
                    <SpanField value={span} onChange={setSpan} />
                    <div className={owner && mates.length > 0 ? "grid grid-cols-2 items-start gap-3" : ""}>
                      <Field label="Fiche">
                        <ChoiceField value={client} placeholder="Aucune" options={[{ value: "", label: "Aucune" }, ...fiches.map((row) => ({ value: String(row.id), label: row.name }))]} onChange={setClient} />
                      </Field>
                      {owner && mates.length > 0 ? <ShareLine plain mates={mates} shareIds={shareIds} setShareIds={setShareIds} /> : null}
                    </div>
                    <Field label="Rappel">
                      <ChoiceField
                        value={reminder}
                        placeholder="Aucun"
                        options={REMINDERS.map((row) => ({ value: row.minutes === null ? "" : String(row.minutes), label: row.label }))}
                        onChange={(value) => { setReminder(value); if (value && !mail && !sms) setMail(true); }}
                      />
                    </Field>
                    <div className="flex gap-2">
                      <button type="button" className={mail ? "crm-soft h-11 flex-1 rounded-full border text-sm font-semibold" : "crm-pill h-11 flex-1 rounded-full text-sm font-semibold"} onClick={() => setMail((value) => !value)}>E-mail</button>
                      <button type="button" className={sms ? "crm-soft h-11 flex-1 rounded-full border text-sm font-semibold" : "crm-pill h-11 flex-1 rounded-full text-sm font-semibold"} onClick={() => setSms((value) => !value)}>SMS</button>
                    </div>
                    {wasMirror ? (
                      <Switch
                        on={followAgenda}
                        label="Suivre l’agenda"
                        onClick={() => {
                          setFollowAgenda((value) => {
                            const next = !value;
                            if (!next) setPlaceAgenda(false);
                            return next;
                          });
                        }}
                      />
                    ) : null}
                    {showPlaceAgenda ? (
                      <>
                        <Switch on={placeAgenda} label="Placer dans l’agenda" onClick={() => setPlaceAgenda((value) => !value)} />
                        {placeAgenda ? (
                          <ChoiceField
                            value={agendaCal}
                            placeholder={agendas.length === 0 ? "Aucun agenda" : "Choisir"}
                            options={agendas.map((item) => ({ value: item.id, label: item.name }))}
                            onChange={setAgendaCal}
                          />
                        ) : null}
                      </>
                    ) : null}
                    {draft || (wasMirror && followAgenda) ? null : (
                      <>
                        <p className="text-sm text-slate-600">{periodLabel(period) || "Aucune récurrence"}</p>
                        <button type="button" className="h-11 w-full rounded-full border border-slate-200 text-sm font-semibold" onClick={() => void openPeriod()}>
                          {period ? "Modifier la récurrence" : "Lancer une récurrence"}
                        </button>
                        {period ? (
                          <button type="button" className="h-11 w-full rounded-full text-sm font-semibold text-rose-700" disabled={busy} onClick={() => { if (!confirmOff) { setConfirmOff(true); return; } void clearPeriod(); }}>
                            {confirmOff ? "Confirmer la désactivation" : "Désactiver la récurrence"}
                          </button>
                        ) : null}
                      </>
                    )}
                    {catalog.map((prop) => (
                      <Field key={prop.key} label={prop.label}>
                        <ChoiceField
                          value={props[prop.key] || ""}
                          placeholder="—"
                          options={[{ value: "", label: "—" }, ...prop.values.map((value) => ({ value: value.key, label: value.label, color: value.color }))]}
                          onChange={(value) => setProp(prop.key, value)}
                        />
                      </Field>
                    ))}
                    {error && !compactCreate ? <p className="text-sm text-rose-700">{error}</p> : null}
                  </div>
                  {!compactCreate ? (
                    <div className="crm-phone-task-note mt-3 rounded-xl border border-slate-200 px-3 pb-3">
                      <span className="shrink-0 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Bloc-note</span>
                      <FichePagePicker fiche={linked} note={note} onUse={(content) => { setNote(content); setPadSeed(content); setPadTick((value) => value + 1); }} />
                      <NotePad ref={pad} key={`${task.id}-${padTick}`} initial={padSeed} kanban portaled upload={linked?.coffre_path ? { folder: linked.coffre_path, ownerId: linked.coffre_owner_id || 0 } : null} onChange={setNote} />
                    </div>
                  ) : null}
                  <div className="crm-task-sheet-foot crm-phone-task-foot">
                    <button type="button" className="h-11 w-full rounded-full crm-fill text-sm font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void save(close)}>
                      {busy ? "En cours…" : "Enregistrer"}
                    </button>
                    <div className="crm-phone-task-row">
                      {!draft ? (
                        <button type="button" className="rounded-full border border-slate-200 text-sm font-semibold disabled:opacity-50" disabled={busy} onClick={() => void save(close, status === "done" ? "todo" : "done")}>
                          {status === "done" ? "Rouvrir" : "Clore"}
                        </button>
                      ) : null}
                      {owner ? (
                      <button
                        type="button"
                        className={confirmDrop ? "rounded-full bg-rose-600 text-sm font-semibold text-white" : "rounded-full border border-slate-200 text-sm font-semibold text-rose-700"}
                        disabled={busy}
                        onClick={() => {
                          if (draft) { close(); return; }
                          if (!confirmDrop) { setConfirmDrop(true); return; }
                          void remove(close);
                        }}
                      >
                        {draft ? "Annuler" : confirmDrop ? "Confirmer" : "Supprimer"}
                      </button>
                      ) : null}
                      {linked ? <button type="button" className="rounded-full border border-slate-200 text-sm font-semibold" onClick={() => { onOpenFiche(linked.id); close(); }}>Fiche</button> : null}
                    </div>
                  </div>
                </>
              ) : (
              <>
              <div className="crm-task-sheet-body mt-4">
                <div className="grid min-h-0 gap-4 md:h-full md:grid-cols-[minmax(18rem,28rem)_minmax(0,1fr)]">
                  <div className="crm-scroll min-h-0 space-y-3 overflow-auto pr-1 md:max-h-full">
                    <section className="rounded-2xl border border-slate-200 p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Programmation</p>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <Field label="Jour"><DateField value={day} onChange={setDay} /></Field>
                        <Field label="Heure">
                          <div className={span === "time" ? "" : "pointer-events-none opacity-40"}>
                            <TimeField value={time} onChange={setTime} />
                          </div>
                        </Field>
                      </div>
                      <div className="mt-3">
                        <SpanField value={span} onChange={setSpan} />
                      </div>
                      {wasMirror ? (
                        <div className="mt-3">
                          <Switch
                            on={followAgenda}
                            label="Suivre l’agenda"
                            onClick={() => {
                              setFollowAgenda((value) => {
                                const next = !value;
                                if (!next) setPlaceAgenda(false);
                                return next;
                              });
                            }}
                          />
                          <p className="mt-1.5 text-xs text-slate-500">
                            {followAgenda
                              ? "Activé : déplacer la carte ou l’événement déplace l’autre."
                              : "Désactivé : la tâche devient indépendante — modifier la date ne suit plus l’Agenda."}
                          </p>
                        </div>
                      ) : null}
                      {showPlaceAgenda ? (
                        <div className="mt-3">
                          <Switch
                            on={placeAgenda}
                            label="Placer dans l’agenda"
                            onClick={() => setPlaceAgenda((value) => !value)}
                          />
                          {placeAgenda ? (
                            <div className="mt-2">
                              <ChoiceField
                                value={agendaCal}
                                placeholder={agendas.length === 0 ? "Aucun agenda" : "Choisir"}
                                options={agendas.map((item) => ({ value: item.id, label: item.name }))}
                                onChange={setAgendaCal}
                              />
                            </div>
                          ) : (
                            <p className="mt-1.5 text-xs text-slate-500">Crée un événement lié dans votre agenda Suite.</p>
                          )}
                        </div>
                      ) : null}
                      <div className="mt-3">
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="min-w-[7.5rem] flex-1">
                            <Field label="Rappel">
                              <ChoiceField
                                value={reminder}
                                placeholder="Aucun"
                                options={REMINDERS.map((row) => ({ value: row.minutes === null ? "" : String(row.minutes), label: row.label }))}
                                onChange={(value) => { setReminder(value); if (value && !mail && !sms) setMail(true); }}
                              />
                            </Field>
                          </div>
                          <button type="button" className={mail ? "crm-soft h-[38px] shrink-0 rounded-full border px-3 text-sm font-semibold" : "crm-pill h-[38px] shrink-0 rounded-full px-3 text-sm font-semibold"} onClick={() => setMail((value) => !value)}>E-mail</button>
                          <button type="button" className={sms ? "crm-soft h-[38px] shrink-0 rounded-full border px-3 text-sm font-semibold" : "crm-pill h-[38px] shrink-0 rounded-full px-3 text-sm font-semibold"} onClick={() => setSms((value) => !value)}>SMS</button>
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500">Sans e-mail ni SMS, le rappel est retiré.</p>
                      </div>
                    </section>
                    {draft || (wasMirror && followAgenda) ? null : (
                      <section className="rounded-2xl border border-slate-200 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                          {period?.placeAgenda ? "Périodicité et agenda" : "Périodicité"}
                        </p>
                        <p className="mt-2 text-sm text-slate-600">{periodLabel(period) || "Aucune récurrence"}</p>
                        {periodProgress(meta, status) ? <p className="mt-1 text-sm crm-ink">{periodProgress(meta, status)}</p> : null}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" onClick={() => void openPeriod()}>
                            {period ? "Modifier la récurrence" : "Lancer une récurrence"}
                          </button>
                          {period ? (
                            <button
                              type="button"
                              className="rounded-full px-3 py-1.5 text-sm font-medium text-rose-700"
                              disabled={busy}
                              onClick={() => {
                                if (!confirmOff) { setConfirmOff(true); return; }
                                void clearPeriod();
                              }}
                            >
                              {confirmOff ? "Confirmer la désactivation" : "Désactiver"}
                            </button>
                          ) : null}
                        </div>
                        {period?.placeAgenda ? <p className="mt-2 text-xs text-slate-500">Occurrences aussi placées dans l’agenda.</p> : null}
                        {period && !period.placeAgenda ? <p className="mt-2 text-xs text-slate-500">Occurrences dans le CRM uniquement — rien dans l’agenda.</p> : null}
                        {period ? <p className="mt-2 text-xs text-slate-500">Chaque échéance est une tâche séparée : la valider n’impute pas les suivantes.</p> : null}
                      </section>
                    )}
                    <div className={owner && mates.length > 0 ? "grid items-start gap-3 sm:grid-cols-2" : ""}>
                      <section className="rounded-2xl border border-slate-200 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Fiche</p>
                        <div className="mt-2">
                          <Field label="Fiche liée">
                            <ChoiceField value={client} placeholder="Aucune" options={[{ value: "", label: "Aucune" }, ...fiches.map((row) => ({ value: String(row.id), label: row.name }))]} onChange={setClient} />
                          </Field>
                        </div>
                      </section>
                      {owner && mates.length > 0 ? <ShareLine mates={mates} shareIds={shareIds} setShareIds={setShareIds} /> : null}
                      {owner ? <div className="sm:col-span-2"><OutsideShareField kind="week" taskId={draft ? "" : task.id} ids={outsideIds} onChange={(ids) => { outsideTouched.current = true; setOutsideIds(ids); }} compact /></div> : null}
                    </div>
                    {catalog.length > 0 ? (
                      <section className="rounded-2xl border border-slate-200 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Propriétés</p>
                        <div className="mt-2 grid gap-3 sm:grid-cols-2">
                          {catalog.map((prop) => (
                            <Field key={prop.key} label={prop.label}>
                              <ChoiceField
                                value={props[prop.key] || ""}
                                placeholder="—"
                                options={[{ value: "", label: "—" }, ...prop.values.map((value) => ({ value: value.key, label: value.label, color: value.color }))]}
                                onChange={(value) => setProp(prop.key, value)}
                              />
                            </Field>
                          ))}
                        </div>
                      </section>
                    ) : null}
                  </div>
                  <div className="crm-task-note flex min-h-64 flex-col rounded-2xl border border-slate-200 p-3 md:min-h-0">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Bloc-note</span>
                    <div className="mt-2">
                      <FichePagePicker fiche={linked} note={note} onUse={(content) => { setNote(content); setPadSeed(content); setPadTick((value) => value + 1); }} />
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200">
                      <NotePad ref={pad} key={`${task.id}-${padTick}`} initial={padSeed} kanban portaled upload={linked?.coffre_path ? { folder: linked.coffre_path, ownerId: linked.coffre_owner_id || 0 } : null} onChange={setNote} />
                    </div>
                  </div>
                </div>
                {error && !compactCreate ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
              </div>
              <div className="crm-task-sheet-foot flex flex-wrap items-center gap-2">
                {owner ? (
                <button
                  type="button"
                  aria-label={confirmDrop ? "Confirmer la suppression" : "Supprimer"}
                  title={confirmDrop ? "Confirmer la suppression" : "Supprimer"}
                  className={confirmDrop ? "grid h-9 w-9 place-items-center rounded-full bg-rose-600 text-white" : "grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600"}
                  disabled={busy}
                  onClick={() => {
                    if (draft) { close(); return; }
                    if (!confirmDrop) { setConfirmDrop(true); return; }
                    void remove(close);
                  }}
                >
                  <span className="material-icons-round text-[18px] leading-none">delete</span>
                </button>
                ) : null}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {linked ? <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" onClick={() => { onOpenFiche(linked.id); close(); }}>Ouvrir la fiche</button> : null}
                  {!draft ? (
                    <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-sm font-semibold disabled:opacity-50" disabled={busy} onClick={() => void save(close, status === "done" ? "todo" : "done")}>
                      <span className="material-icons-round text-[16px] leading-none" aria-hidden>{status === "done" ? "replay" : "check"}</span>
                      {status === "done" ? "Rouvrir" : "Clore la tâche"}
                    </button>
                  ) : null}
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void save(close)}>
                    <span className="material-icons-round text-[16px] leading-none" aria-hidden>save</span>
                    {busy ? "En cours…" : "Enregistrer"}
                  </button>
                </div>
              </div>
              </>
              )}
            </div>
          </div>

          {periodOpen && !draft ? (
            <PeriodSheet
              task={{
                ...task,
                title: title.trim() || task.title,
                description: note,
                status,
                start: `${day || taskDay(task)} ${spanBounds(span, time || "09:00").start}:00`,
                meta: { ...meta, daySpan: span, allDay: 0, agendaEnd: `${spanBounds(span, time || "09:00").end}:00` },
              }}
              onClose={() => setPeriodOpen(false)}
              onChange={async (row, patch) => {
                await saveWeekTask(row, patch);
                setMeta(patch.meta !== undefined ? { ...patch.meta } : { ...row.meta });
                if (patch.status !== undefined) setStatus(patch.status);
                notifyWeekChange();
              }}
            />
          ) : null}
        </>
      )}
    </Modal>
  );
}

function ShareLine({ mates, shareIds, setShareIds, plain = false }: { mates: Array<{ id: number; name: string }>; shareIds: number[]; setShareIds: (value: number[] | ((current: number[]) => number[])) => void; plain?: boolean }) {
  const field = (
    <Field label={plain ? "Partager" : "Partager avec"}>
      <MultiChoiceField
        values={shareIds.map(String)}
        emptyLabel="Aucune"
        options={mates.map((mate) => ({ value: String(mate.id), label: mate.name }))}
        onChange={(values) => setShareIds(values.map((value) => Number(value)).filter((id) => id > 0))}
      />
    </Field>
  );
  if (plain) return field;
  return (
    <section className="rounded-2xl border border-slate-200 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Partager</p>
      <div className="mt-2">{field}</div>
    </section>
  );
}

function Switch({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-left text-sm" onClick={onClick}>
      <span>{label}</span>
      <span className={on ? "crm-fill relative h-6 w-11 shrink-0 rounded-full transition" : "relative h-6 w-11 shrink-0 rounded-full bg-slate-300 transition"}>
        <span className={on ? "absolute top-0.5 left-5 h-5 w-5 rounded-full bg-white shadow transition" : "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition"} />
      </span>
    </button>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const RESERVED = new Set(["reminder", "notify_mail", "notify_sms", "repeat", "allDay", "daySpan", "crmFicheId", "crmBoard", "crmBoardProps", "orderByDate", "pending", "agendaCalendarId", "agenda_calendar_id", "marquer_agenda", "repeatStartDate", "seriesId", "occurrenceIndex", "occurrenceTotal", "seriesUntil", "agendaMirror", "agendaEventId", "agendaCalendarColorHex", "agendaEnd", "padBlocks"]);

function statusFromTask(status: string, meta: Record<string, unknown>): string {
  const raw = metaText(meta.statut);
  if (raw) return raw;
  if (status === "done") return "done";
  if (status === "inprogress") return "inprogress";
  if (status === "pending" || meta.pending === true) return "pending";
  return "todo";
}

function taskStatusFromProp(value: string): string {
  if (value === "done" || value === "fait") return "done";
  if (value === "inprogress" || value === "en_cours") return "inprogress";
  if (value === "pending" || value === "en_attente") return "pending";
  if (value === "rdv") return "todo";
  return "todo";
}

function flag(value: unknown): boolean {
  return value === true || value === 1 || value === "true";
}

function plainNote(value: string): string {
  return value.split("<!--BLOCKS_JSON")[0].trim();
}

function formatNoteWhen(day: string, span: DaySpan, bounds: { start: string; end: string }): string {
  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const nice = match ? `${match[3]}/${match[2]}/${match[1]}` : day;
  const label = DAY_SPANS.find((row) => row.id === span)?.label || "Heure";
  if (span === "time") return `${nice} · ${frHour(bounds.start)}`;
  return `${nice} · ${label} (${frHour(bounds.start)}–${frHour(bounds.end)})`;
}

function frHour(hhmm: string): string {
  return hhmm.replace(/^0/, "").replace(":", "h");
}

function SpanField({ value, onChange }: { value: DaySpan; onChange: (span: DaySpan) => void }) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {DAY_SPANS.map((row) => (
          <button
            key={row.id}
            type="button"
            className={value === row.id ? "crm-soft h-12 rounded-xl border px-2 text-sm font-semibold" : "crm-pill h-12 rounded-xl border border-transparent px-2 text-sm font-semibold"}
            onClick={() => onChange(row.id)}
          >
            <span className="block leading-tight">{row.label}</span>
            <span className="block text-[10px] font-medium leading-tight opacity-80">
              {row.start ? `${frHour(row.start)} – ${frHour(row.end)}` : "1 h à partir de l’heure"}
            </span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-500">Posée dans l’agenda lié au type de RDV, la plage retire ces créneaux de Mes RDV.</p>
    </div>
  );
}

function metaText(value: unknown): string {
  if (value === null || value === undefined || value === false) return "";
  return String(value);
}

function statusClass(status: string): string {
  if (status === "done") return "rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700";
  if (status === "inprogress") return "rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700";
  if (status === "pending") return "rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700";
  return "rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600";
}

function ficheOf(fiches: CrmFiche[], task: WeekTask): CrmFiche | undefined {
  const id = Number(task.meta.crmFicheId);
  if (id > 0) {
    const row = fiches.find((fiche) => fiche.id === id);
    if (row) return row;
  }
  return matchFiche(fiches, task.client_name);
}

function matchFiche(fiches: CrmFiche[], name: string): CrmFiche | undefined {
  const key = name.trim().toLocaleLowerCase("fr");
  if (!key) return undefined;
  return fiches.find((row) => row.name.trim().toLocaleLowerCase("fr") === key);
}

function reminderValue(task: WeekTask): string {
  const value = task.meta.reminder;
  if (value === null || value === undefined || value === "" || value === "null") return "";
  const minutes = Number(value);
  return Number.isFinite(minutes) ? String(minutes) : "";
}

const GAP = 8;
const PEEK = 0.42;
const LEAD = 8;
const WHEEL_NOTCH = 80;

function wheelPixels(event: WheelEvent): number {
  const raw = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
  if (event.deltaMode === 1) return raw * 16;
  if (event.deltaMode === 2) return raw * 400;
  return raw;
}

type DragGhost = { id: string; title: string; x: number; y: number; width: number };
type DropSlot = { day: string; index: number };
type WeekJump = { dir: 1 | -1; from: Date; to: Date; go: boolean };

function slotUnder(x: number, y: number, dragId: string): DropSlot | null {
  const node = document.elementFromPoint(x, y);
  const column = node?.closest("[data-day]");
  const day = column?.getAttribute("data-day") || "";
  if (!day) return null;
  const cards = [...(column?.querySelectorAll<HTMLElement>("[data-task-id]") || [])].filter((card) => card.dataset.taskId !== dragId);
  for (let index = 0; index < cards.length; index += 1) {
    const rect = cards[index].getBoundingClientRect();
    if (y < rect.top + rect.height / 2) return { day, index };
  }
  return { day, index: cards.length };
}

function clampIndex(task: WeekTask, rows: WeekTask[], index: number): number {
  const openCount = rows.filter((row) => row.status !== "done").length;
  if (task.status === "done") return Math.max(openCount, Math.min(index, rows.length));
  return Math.max(0, Math.min(index, openCount));
}

function clampSlot(task: WeekTask, slot: DropSlot, tasks: WeekTask[]): DropSlot {
  const rows = tasksOf(tasks, slot.day).filter((row) => row.id !== task.id);
  return { day: slot.day, index: clampIndex(task, rows, slot.index) };
}

function defaultUntil(day: string): string {
  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date();
  date.setMonth(date.getMonth() + 3);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const value = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${value}`;
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d={dir === "left" ? "M14.5 6.5 9 12l5.5 5.5" : "M9.5 6.5 15 12l-5.5 5.5"} />
    </svg>
  );
}

function daySpan(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function startOf(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, count: number): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  copy.setDate(copy.getDate() + count);
  return copy;
}

function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function dayTitle(date: Date): string {
  return date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}

function tasksOf(tasks: WeekTask[], key: string): WeekTask[] {
  return tasks
    .filter((task) => taskDay(task) === key)
    .sort((a, b) => {
      // Validées en bas ; sinon l’ordre manuel de la colonne (drag).
      const done = Number(a.status === "done") - Number(b.status === "done");
      if (done) return done;
      const aOrder = orderRank(a, key);
      const bOrder = orderRank(b, key);
      if (aOrder !== bOrder) {
        if (aOrder === 999999) return 1;
        if (bOrder === 999999) return -1;
        return aOrder - bOrder;
      }
      return taskClock(a).localeCompare(taskClock(b)) || a.title.localeCompare(b.title, "fr");
    });
}
